import React, { useCallback } from "react";
import { Drawer, Layout, Menu } from "antd";
import "./Nav.css";
import { sidebarItems } from "./SidebarData";
import { Link, useLocation } from "react-router-dom";
import useUser from "../../../hooks/useUser";
import { useLayout } from "../../../context/LayoutContext";
import { MessageCircle } from "lucide-react";

const { Sider } = Layout;

const Sidebar = ({ isMobile = false }) => {
  const location = useLocation();
  const { decoded } = useUser();
  const { mobileSidebarOpen, closeMobileSidebar } = useLayout();

  // Defines modules accessible to non-admin users
  const userModules = [
    "dashboard",
    "chats",
    "contacts",
    "campaigns",
    "templates",
    "bookings",
    "bots",
    "whatsapp",
    "billing",
    "user_subscriptions",
    "user_addons",
    "browse_addons",
    "settings",
  ];

  const findSelectedKey = useCallback((items, path) => {
    const item = items.find(item => item.url === path);
    return item ? item.id.toString() : null;
  }, []);

  const selectedKey = findSelectedKey(sidebarItems, location.pathname);

  // Filter items based on user role
  const filteredItems = decoded?.role === "admin"
    ? sidebarItems
    : sidebarItems.filter((item) => userModules.includes(item.module));

  // Group items by parent_id
  const parentItems = filteredItems.filter(item => item.parent_id === null);
  const childItems = filteredItems.filter(item => item.parent_id !== null);

  const renderMenuItem = (menu, hasChildren = false) => {
    const isActive = selectedKey === menu.id.toString();
    const children = childItems.filter(child => child.parent_id === menu.id);

    if (hasChildren && children.length > 0) {
      return (
        <Menu.SubMenu
          key={menu.id}
          icon={<span className="nav-icon">{menu.icon}</span>}
          title={<span className="nav-label">{menu.label}</span>}
        >
          {children.map(child => (
            <Menu.Item
              key={child.id}
              icon={<span className="nav-icon">{child.icon}</span>}
              onClick={() => {
                if (isMobile) closeMobileSidebar();
              }}
            >
              <Link to={child.url} className="nav-link">
                <span className="nav-label">{child.label}</span>
              </Link>
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      );
    }

    return (
      <Menu.Item
        key={menu.id}
        icon={<span className="nav-icon">{menu.icon}</span>}
        className={isActive ? "ant-menu-item-selected" : ""}
        onClick={() => {
          if (isMobile) closeMobileSidebar();
        }}
      >
        <Link to={menu.url} className="nav-link">
          <span className="nav-label">{menu.label}</span>
        </Link>
      </Menu.Item>
    );
  };

  const sidebarContent = (
    <div className="premium-sidebar-inner" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
      <div className="sidebar-logo">
        <div className="logo-box">
          <MessageCircle size={20} color="white" />
        </div>
        <span className="logo-text">Chatfuse</span>
      </div>

      <div className="sidebar-menu-wrapper" style={{ flex: 1, overflowY: 'auto' }}>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          theme="dark"
          inlineIndent={20}
          style={{ background: 'transparent', border: 'none' }}
        >
          {parentItems.map((item) => {
            const hasChildren = childItems.some(child => child.parent_id === item.id);
            return renderMenuItem(item, hasChildren);
          })}
        </Menu>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={closeMobileSidebar}
        open={mobileSidebarOpen}
        bodyStyle={{ padding: 0 }}
        drawerStyle={{ backgroundColor: '#0f172a' }}
        width={260}
        closable={false}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      width={240}
      className="premium-sidebar no-collapse"
      theme="dark"
      trigger={null}
      collapsible={false}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1001,
      }}
    >
      {sidebarContent}
    </Sider>
  );
};

export default Sidebar;
